import { useState, useCallback, useEffect, RefObject } from 'react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MapPin, Globe, Search } from 'lucide-react';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { useDebounce } from 'use-debounce';
import { toast } from 'sonner';
import type { MapRef } from 'react-map-gl/mapbox';
import {
	FeatureResult,
	GroupedResults,
	Layer, LayerSearchConfig
} from './types';
import { SearchInput } from '@/components/search-input/SearchInput';
import { LAYERS_UI_CONFIG} from './constants';
import { TIME_CONSTANTS_IN_MS } from '@/constants';
import { waitFor } from '@/utils/time';
import { SEARCHABLE_LAYERS_CONFIG, featureMatchesSearchQuery } from '@/routes/map/Utils.ts';

interface FeatureSearchControlProps {
	mapRef: RefObject<MapRef>;
	onFeatureSelect: (feature: FeatureResult) => void;
}

export function FeatureSearchControl({ mapRef, onFeatureSelect }: FeatureSearchControlProps) {
	const [searchQuery, setSearchQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [results, setResults] = useState<GroupedResults>({});
	const [isSearching, setIsSearching] = useState(false);
	const [globalSearch, setGlobalSearch] = useState(true);
	const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);

	const { isTablet, isMobile } = useIsDesktopSize();

	const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

	const searchLayerFeatures = useCallback(
		(map: mapboxgl.Map, layerType: Layer, config: LayerSearchConfig, query: string): FeatureResult[] => {
			const features: FeatureResult[] = [];

			try {
				const sourceFeatures = map.querySourceFeatures(config.sourceId, {
					sourceLayer: config.sourceLayer
				});

				sourceFeatures.forEach(feature => {
					if (
						feature.geometry.type === 'Point' ||
						(feature.geometry.type === 'LineString' && feature.properties)
					) {
						const searchableFields = config.getSearchableFields(feature.properties);

						if (featureMatchesSearchQuery(searchableFields, query)) {
							const [longitude, latitude] =
								feature.geometry.type === 'Point'
									? (feature.geometry as GeoJSON.Point).coordinates
									: (feature.geometry as GeoJSON.LineString).coordinates[0];

							const name = config.getName(feature.properties);
							const id = config.getId(feature.properties);

							features.push({
								id,
								name,
								latitude,
								longitude,
								layerType,
								properties: feature.properties
							});
						}
					}
				});
			} catch {
				toast.error(`Failed to search ${LAYERS_UI_CONFIG[layerType].name}`, {
					description: 'There was an issue searching this layer. Please try again.'
				});
			}

			return features;
		},
		[]
	);

	const searchFeatures = useCallback(
		async (query: string) => {
			if (!mapRef.current || !query.trim()) {
				setResults({});
				setExpandedAccordions([]);
				return;
			}

			setIsSearching(true);

			try {
				const map = mapRef.current.getMap();

				if (globalSearch) {
					map.fitBounds(
						[
							[-180, -90],
							[180, 90]
						],
						{ duration: 0 }
					);

					await waitFor(TIME_CONSTANTS_IN_MS.ONE_SECOND);
				}

				const allFeatures: FeatureResult[] = [];

				Object.entries(SEARCHABLE_LAYERS_CONFIG).forEach(([layerType, config]) => {
					const layerFeatures = searchLayerFeatures(map, layerType as Layer, config, query);
					allFeatures.push(...layerFeatures);
				});

				const grouped: GroupedResults = groupByLayerType(allFeatures);

				setResults(grouped);

				const layersWithResults = Object.keys(grouped);
				setExpandedAccordions(layersWithResults);
			} catch {
				toast.error('Search failed', {
					description: 'Unable to search features. Please check your connection and try again.'
				});
				setResults({});
				setExpandedAccordions([]);
			} finally {
				setIsSearching(false);
			}
		},
		[mapRef, globalSearch, searchLayerFeatures]
	);

	useEffect(() => {
		if (!debouncedSearchQuery.trim()) {
			setResults({});
			setExpandedAccordions([]);
			setIsSearching(false);
			return;
		}

		searchFeatures(debouncedSearchQuery);
	}, [debouncedSearchQuery, globalSearch, searchFeatures]);

	const onResultedFeatureClick = useCallback(
		(feature: FeatureResult) => {
			if (!mapRef.current) {
				setIsOpen(false);
				return;
			}

			mapRef.current.flyTo({
				center: [feature.longitude, feature.latitude],
				zoom: 10,
				duration: 1000
			});

			const map = mapRef.current.getMap();

			const onMoveEnd = () => {
				map.off('moveend', onMoveEnd);
				onFeatureSelect(feature);
			};

			map.on('moveend', onMoveEnd);

			setIsOpen(false);
		},
		[mapRef, onFeatureSelect]
	);

	const renderFeatureItem = useCallback(
		(feature: FeatureResult) => {
			const config = LAYERS_UI_CONFIG[feature.layerType as Layer];

			const Icon = config.Icon || MapPin;

			return (
				<div
					key={`${feature.layerType}-${feature.id}`}
					className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer rounded-md transition-colors"
					onClick={() => onResultedFeatureClick(feature)}
				>
					<Icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
					<div className="flex-1 min-w-0">
						<div className="font-medium text-sm truncate">{feature.name}</div>
						<div className="text-xs text-slate-500 dark:text-slate-400">
							ID: {feature.id} • {feature.latitude.toFixed(6)}, {feature.longitude.toFixed(6)}
						</div>
					</div>
				</div>
			);
		},
		[onResultedFeatureClick]
	);

	const renderResults = useCallback(() => {
		if (Object.keys(results).length === 0) {
			return null;
		}

		const defaultValues = Object.keys(results);

		return (
			<div className="space-y-1">
				<Accordion
					type="multiple"
					value={expandedAccordions}
					onValueChange={setExpandedAccordions}
					defaultValue={defaultValues}
					className="w-full"
				>
					{Object.entries(results).map(([layerType, features]) => {
						const config = LAYERS_UI_CONFIG[layerType as Layer];

						return (
							<AccordionItem key={layerType} value={layerType} className="border-none">
								<AccordionTrigger className="px-3 py-2 bg-slate-50 dark:bg-slate-800 hover:no-underline rounded-md">
									<div className="flex items-center gap-2">
										<Badge
											variant="secondary"
											className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
										>
											{config?.name || layerType}
										</Badge>
										<span className="text-xs text-slate-500">
											{features.length} result{features.length !== 1 ? 's' : ''}
										</span>
									</div>
								</AccordionTrigger>
								<AccordionContent className="pb-0">
									<div className="space-y-1 mt-1">{features.map(renderFeatureItem)}</div>
								</AccordionContent>
							</AccordionItem>
						);
					})}
				</Accordion>
			</div>
		);
	}, [results, expandedAccordions, renderFeatureItem]);

	const renderHeader = useCallback(
		() => (
			<TooltipProvider>
				<div className="flex items-center gap-2 px-3 py-2 border-b">
					<Globe className="h-4 w-4 text-slate-500" />
					<span className="text-sm font-medium">Global Search</span>
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="ml-auto">
								<Switch checked={globalSearch} onCheckedChange={setGlobalSearch} />
							</div>
						</TooltipTrigger>
						<TooltipContent>
							<p>Turning this on will load all features on the map</p>
						</TooltipContent>
					</Tooltip>
				</div>
			</TooltipProvider>
		),
		[globalSearch]
	);

	const totalResults = Object.values(results).reduce((total, features) => total + features.length, 0);

	if (isTablet || isMobile) {
		return (
			<div className="space-y-4">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
					<Input
						placeholder="Search features..."
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						className="pl-10"
					/>
				</div>

				{renderHeader()}

				{(searchQuery.trim() || totalResults > 0) && (
					<div className="border rounded-lg bg-white dark:bg-slate-900">
						<ScrollArea className="h-[300px]">
							{isSearching ? (
								<div className="p-4 text-center text-sm text-slate-500">Searching...</div>
							) : !searchQuery.trim() ? (
								<div className="p-4 text-center text-sm text-slate-500">
									Start typing to search features...
								</div>
							) : totalResults === 0 ? (
								<div className="p-4 text-center text-sm text-slate-500">
									No features found for "{searchQuery}"
								</div>
							) : (
								renderResults()
							)}
						</ScrollArea>
					</div>
				)}
			</div>
		);
	}

	return (
		<SearchInput
			value={searchQuery}
			onValueChange={setSearchQuery}
			placeholder="Search features..."
			isLoading={isSearching}
			isOpen={isOpen}
			onOpenChange={setIsOpen}
			renderResults={renderResults}
			renderHeader={renderHeader}
			emptyMessage={`No features found for "${searchQuery}"`}
			className="w-56"
			popoverClassName="w-80"
			totalResults={totalResults}
		/>
	);
}

function groupByLayerType(allFeatures: FeatureResult[]): GroupedResults {
	return allFeatures.reduce((acc, feature) => {
		const key = feature.layerType;
		if (!acc[key]) {
			acc[key] = [];
		}
		acc[key].push(feature);
		return acc;
	}, {} as GroupedResults);
}
